import type {
    DynamicFilterDescriptionOption,
    DynamicFilterFieldDescription,
} from "app/pages/sales-intelligence/domain/search/types/dynamic-filter-options";
import { abbrNumberFilter } from "app/filters/ngFilters";
import type { IRelativeDateRangeOptions } from "app/pages/sales-intelligence/types/range";
import { assertNever } from "app/pages/sales-intelligence/utils/errors/assertions";

import type { DynamicFieldDescriptor } from "../../types/dynamicFieldDescriptor";
import type {
    DynamicComboboxFilterOption,
    DynamicComboboxFilterOptions,
    DynamicNumberRangeOptions,
    DynamicRelativeDateRangeOptions,
} from "../../types/filterOptions";

const toComboboxOptions = (values: readonly DynamicFilterDescriptionOption[]): readonly DynamicComboboxFilterOption[] =>
    values.filter((option) => option.key !== "").map((option) => ({ id: option.key, label: option.value }));

const toComboboxFilterOptions = (field: {
    readonly name: string;
    readonly values: readonly DynamicFilterDescriptionOption[];
}): DynamicComboboxFilterOptions => ({
    options: toComboboxOptions(field.values),
    label: field.name,
    placeholder: field.name,
});

const toRelativeDateRangeFilterOptions = (field: {
    readonly name: string;
    readonly values: IRelativeDateRangeOptions;
}): DynamicRelativeDateRangeOptions => ({
    ranges: field.values.ranges,
    rangeTypes: field.values.rangeTypes,
    label: field.name,
});

const toNumberRangeFilterOptions = (field: {
    readonly key: string;
    readonly name: string;
    readonly values: readonly number[];
}): DynamicNumberRangeOptions => ({
    fieldName: field.key,
    buckets: field.values,
    label: field.name,
    formatValue: (value) => abbrNumberFilter()(value),
});

/** @throws SiError on unsuported `widget` or `dataType` */
export const toDescriptor = (field: DynamicFilterFieldDescription): DynamicFieldDescriptor | undefined => {
    switch (field.dataType) {
        case "enum":
            switch (field.widget) {
                case "combobox":
                    return {
                        key: field.key,
                        widget: "enumCombobox",
                        dataType: field.dataType,
                        options: toComboboxFilterOptions(field),
                        metadata: field.metadata,
                    };
                default:
                    return assertNever(field.widget, "descriptorTransform:enum");
            }
        case "enumArray":
            switch (field.widget) {
                case "combobox":
                    return {
                        key: field.key,
                        widget: "enumArrayCombobox",
                        dataType: field.dataType,
                        options: toComboboxFilterOptions(field),
                        metadata: field.metadata,
                    };
                default:
                    return assertNever(field.widget, "descriptorTransform:enumArray");
            }
        case "date":
            switch (field.widget) {
                case "combobox":
                    return {
                        key: field.key,
                        widget: "relativeDateRange",
                        dataType: field.dataType,
                        options: toRelativeDateRangeFilterOptions(field),
                        metadata: field.metadata,
                    };
                default:
                    return assertNever(field.widget, "descriptorTransform:date");
            }
        case "numberRange":
            return {
                key: field.key,
                widget: "numberRange",
                dataType: field.dataType,
                options: toNumberRangeFilterOptions(field),
                metadata: field.metadata,
            };
        default:
            return assertNever(field, "descriptorTransform");
    }
};
